import IconButton from '@/components/Buttons/IconButton';
import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import Volume from '@/images/Volume.svg';
import VolumeCross from '@/images/VolumeCross.svg';
import theme from '@/theme';
import styled from '@emotion/styled';
import CloseIcon from '@mui/icons-material/Close';
import { ButtonBase } from '@mui/material';
import { useRef, useState } from 'react';

const Container = styled.div`
    position: relative;
    width: 100%;
    height: 100%;
    min-height: 400px;
`;

const Video = styled.video`
    width: 100%;
    height: 100%;
    object-fit: cover;
`;

const MuteButton = styled(ButtonBase)<{ muted: boolean }>`
    position: absolute;
    top: 16px;
    left: 16px;
    z-index: 10;
    height: 32px;
    gap: 4px;
    border-radius: 8px;
    display: flex;

    padding: 4px 12px;
    color: ${({ theme }) => theme.colors.secondary[900]};
    background-color: ${({ theme, muted }) => (muted ? theme.colors.white : 'rgba(9, 6, 4, 0.80)')};
    border: 1px solid ${({ theme, muted }) => (muted ? theme.colors.black[300] : 'transparent')};
    backdrop-filter: blur(8px);

    color: ${({ theme, muted }) => (muted ? theme.colors.secondary[900] : theme.colors.white)};
    font-family: Manrope;
    font-size: 14px;
    font-style: normal;
    font-weight: 700;
    line-height: 20px;

    :hover {
        background-color: ${({ theme, muted }) => (muted ? theme.colors.black[200] : 'rgba(9, 6, 4, 1)')};
    }
`;

const CloseButton = styled(IconButton)`
    position: absolute;
    top: 16px;
    right: 16px;
    z-index: 10;
    background-color: rgba(9, 6, 4, 0.8) !important;
    color: #fff !important;
    width: 32px;
    height: 32px;
    border-radius: 8px;

    &:hover {
        background-color: rgba(9, 6, 4, 1) !important;
    }
`;

interface Props {
    previewUrl: string;
    onClose?: () => void;
}

export const SentimentPreview = ({ previewUrl, onClose }: Props) => {
    const [isMuted, setIsMuted] = useState(true);
    const videoRef = useRef<HTMLVideoElement>(null);

    const handleMuteToggle = () => {
        if (videoRef.current) {
            const newMutedState = !isMuted;
            videoRef.current.muted = newMutedState;
            setIsMuted(newMutedState);
        }
    };

    return (
        <Container>
            <Video ref={videoRef} src={previewUrl} autoPlay loop muted={isMuted} playsInline />
            <MuteButton onClick={handleMuteToggle} muted={isMuted}>
                {isMuted ? (
                    <MaskedIcon src={VolumeCross} size="24px" color={theme.colors.secondary[900]} />
                ) : (
                    <MaskedIcon src={Volume} size="24px" color={theme.colors.white} />
                )}
                {isMuted ? 'Unmute' : 'Mute'}
            </MuteButton>
            {onClose && (
                <CloseButton ariaLabel="Close" onClick={onClose}>
                    <CloseIcon sx={{ color: theme.colors.white, fontSize: '20px' }} />
                </CloseButton>
            )}
        </Container>
    );
};
